/**
 * React context for shared Products finance hub state (dashboard cards + commerce modal).
 */
import React, { createContext, useContext } from 'react';
import { Modal } from 'antd';
import FinancePartnerChecklistModal from './FinancePartnerChecklistModal';
import { useProductsFinanceHubController } from './useProductsFinanceHubController';
import type {
    ProductsFinanceHubContextValue,
    ProductsFinanceHubProviderProps,
} from './ProductsFinanceHubContext.types';

export const ProductsFinanceHubContext = createContext<ProductsFinanceHubContextValue | null>(null);

export function useProductsFinanceHubContext(): ProductsFinanceHubContextValue {
    const ctx = useContext(ProductsFinanceHubContext);
    if (!ctx) {
        throw new Error('useProductsFinanceHubContext must be used within ProductsFinanceHubProvider');
    }
    return ctx;
}

export const ProductsFinanceHubProvider: React.FC<ProductsFinanceHubProviderProps> = ({
    salesForceId,
    children,
}) => {
    const value = useProductsFinanceHubController({ salesForceId });
    const {
        theme,
        partnerModal,
        setPartnerModal,
        goodLeapExistingApp,
        setGoodLeapExistingApp,
        toggleProductIncluded,
        noticeContext,
    } = value;

    return (
        <ProductsFinanceHubContext.Provider value={value}>
            {noticeContext}
            {children}
            {partnerModal ? (
                <FinancePartnerChecklistModal
                    open={!!partnerModal}
                    partnerName={partnerModal}
                    onClose={() => setPartnerModal(null)}
                />
            ) : null}
            {goodLeapExistingApp ? (
                <Modal
                    open
                    zIndex={13200}
                    centered
                    maskClosable
                    rootClassName={`obd-theme-${theme} obd-generate-quote-lightreach-sync`}
                    okText="Yes"
                    cancelText="No"
                    onCancel={() => setGoodLeapExistingApp(null)}
                    onOk={() => {
                        setGoodLeapExistingApp(null);
                        toggleProductIncluded(goodLeapExistingApp.ProductTypeName, false);
                    }}
                >
                    There is an existing GoodLeap application (
                    <b>{goodLeapExistingApp.Finance_Partner_Sync_LeadID}</b>) for{' '}
                    {goodLeapExistingApp.ProductTypeName}. If you switch to another finance partner or unselect the{' '}
                    {goodLeapExistingApp.ProductTypeName} product, you will need assistance from BT to re-enable{' '}
                    {goodLeapExistingApp.ProductTypeName} with its original financing method. Do you want to proceed
                    with this change?
                </Modal>
            ) : null}
        </ProductsFinanceHubContext.Provider>
    );
};
